import { useState } from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  CreateClosingPeriodFrequencyDocument,
  SignedInvoicesPageDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import SelectField from 'modules/core/components/SelectField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface ClosingPeriodFrequencyModalParams {
  setOpenModal: (value: boolean) => void
  legalEntityId: number
}

const ClosingPeriodFrequencyModal = ({
  setOpenModal,
  legalEntityId,
}: ClosingPeriodFrequencyModalParams) => {
  const [periodType, setPeriodType] = useState<string>('monthly')
  const { company } = useCurrentSession()

  const onClose = () => {
    setOpenModal(false)
  }

  const [createClosingPeriodFrequency, { loading: submitting }] = useMutation(
    CreateClosingPeriodFrequencyDocument,
    {
      extractorFn: (data) => data.expensesMutations.createExpensesClosingPeriodFrequency,
    }
  )

  const onSubmit = async () => {
    const result = await createClosingPeriodFrequency({
      variables: {
        frequency: periodType,
        legalEntityId: legalEntityId,
        organization: 'aeat',
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: SignedInvoicesPageDocument,
          variables: {
            companyId: company.id,
            legalEntityId: legalEntityId,
          },
        },
      ],
    })

    const handled = handleResult(result, (response) => {
      if (response.messages) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_error'),
        })
      }
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })

      onClose()
    }
  }

  const onChangeEvent = (value: string) => {
    setPeriodType(value)
  }

  const optionsForSelect = () => {
    return [
      {
        label: i18n.t('expenses.settings.signed_invoices.modal.options.frequency.monthly'),
        value: 'monthly',
      },
      {
        label: i18n.t('expenses.settings.signed_invoices.modal.options.frequency.quarterly'),
        value: 'quarterly',
      },
    ]
  }

  return (
    <Modal
      illustration="calendar"
      title={i18n.t('expenses.settings.signed_invoices.modal.frequency.title')}
      description={i18n.t('expenses.settings.signed_invoices.modal.frequency.description')}
      onClose={() => onClose()}
      onSubmit={onSubmit}
      footer={
        <Box flexDirection="row" gap="s16">
          <Button type="secondary" onClick={onClose}>
            {i18n.t('expenses.settings.signed_invoices.modal.cancel')}
          </Button>
          <Button submit disabled={submitting}>
            {i18n.t('expenses.settings.signed_invoices.modal.frequency.submit')}
          </Button>
        </Box>
      }
    >
      <SelectField
        mandatory
        label={i18n.t('expenses.settings.signed_invoices.modal.frequency.select')}
        options={optionsForSelect()}
        onChange={onChangeEvent}
        value={periodType}
      />
    </Modal>
  )
}

export default ClosingPeriodFrequencyModal
